import { TDataClick, TDataView } from './types';
import { getQueryString, getPageTitle } from '@common/utils/common';
import { AqAdInfoItem } from '@common/typings/voss';
import { getCookie } from '@common/stores/cookie';
import { useBaseStore } from '@common/stores/storage';
import { getConfigData } from '@fly/air-tag/vue';
import { getConfigData as newGetConfigData, airTagReady } from '@msxf/air-tag/vue';
import dsBridge from '@common/assets/js/dsbridge';
import { msSessionStorage } from '@common/utils/cache';
import { isArray } from 'lodash-es';

/**
 * 拓展埋点字段, 新旧埋点标记
 */
const extraTraceData = {
  new_or_old_system: 'new',
  gray_status: process.env?.IS_PRODUCTION
    ? document.body.dataset?.envName || getCookie('inlinegray') || 'online'
    : 'testNoCare',
};

// 采集器类型
export type TCollector = {
  track: (event: string, data: object) => void;
  quick: (event: string, data: object) => void;
  [prop: string]: any;
};

// 上报数据
export type TTrackData = {
  p_id?: string;
  m_id?: string;
  t_id?: string;
  s_id?: string;
  e_id?: string;
  [key: string]: any;
};

/**
 * 采集器集合
 * 如果想用采集器，需要先把采集器加到集合里
 */
export const collectors = new Set<TCollector>();

export interface IAdData extends AqAdInfoItem {
  eventTrack?: object;
  extendBusinessAttrs?: object;
  other?: object;
}

/**
 * 天权广告数据映射神策埋点数据
 */
function mapAdAttrs(adData?: IAdData | null): object {
  if (adData) {
    const {
      adCode,
      adName,
      adIndex,
      materialCode,
      groupName,
      pageName,
      terminalName,
      termCode,
      // 通用神策字段匹配
      sensorsTrackStr,
      // 通用神策字段匹配
      eventTrack = {},
      // 自定义扩展属性
      extendBusinessAttrs = {},
      // 自定义扩展属性
      other = {},
    } = adData;

    // 解析通用
    const sensorsTrackMapping = {};
    if (sensorsTrackStr) {
      sensorsTrackStr.split(',').forEach((str) => {
        const [sensorsAttr, tqAdInfoKey] = str.split(':');
        if (sensorsAttr && tqAdInfoKey) {
          sensorsTrackMapping[sensorsAttr] = adData[tqAdInfoKey];
        }
      });
    }

    const mapping = Object.assign(
      {
        banner_type: groupName,
        banner_element_name: adIndex,
        banner_id: adCode,
        banner_content: adName,
        material_id: materialCode ?? '无',
        advert_page_name: pageName,
        term_name: terminalName ?? '无',
        team_name: termCode ?? '无',
      },
      eventTrack,
      sensorsTrackMapping,
      extendBusinessAttrs,
      other,
    );
    return mapping;
  }
  return {};
}

/**
 * 获得数据，若为方法则调用获取返回值
 * @param data
 * @returns
 */
function getValueByFunction(data: any): string | number | boolean | undefined {
  if (typeof data === 'function') {
    return data();
  }
  return data;
}

const reportTrackSingle = (event: string, data: TTrackData) => {
  // 获得配置信息
  const { p_id, m_id, t_id, s_id, e_id, page_name, scenes } = getFullTrackData(data);

  // 获得对应值
  const elementP_id = getValueByFunction(p_id);
  const elementM_id = getValueByFunction(m_id);
  const elementT_id = getValueByFunction(t_id);
  const elementS_id = getValueByFunction(s_id);
  const elementE_id = getValueByFunction(e_id);

  const fakeDom: unknown = {
    dataset: {
      elementP_id,
      elementM_id,
      elementT_id,
      elementS_id,
      elementE_id,
    },
  };

  // 获得配置化内容，覆盖合并上报的内容
  const dom: HTMLElement = fakeDom as HTMLElement;
  let configData = {};
  if (airTagReady()) {
    configData = newGetConfigData(dom, event) ?? {};
  } else {
    configData = getConfigData(dom, event) ?? {};
  }
  data = Object.assign({}, data, configData);

  // 上报页面浏览事件时，通知app设置当前页面埋点关键信息
  if (event === 'page_view' && dsBridge.hasNativeMethod('UtilsModule.setWebPageBuriedParams')) {
    dsBridge.call(
      'UtilsModule.setWebPageBuriedParams',
      JSON.stringify({
        page_name: getValueByFunction(page_name),
        scenes: getValueByFunction(scenes),
        p_id: elementP_id,
      }),
    );
  }

  // $pageview事件特殊处理
  if (event === '$pageview') {
    return reportView(data);
  }

  console.info(
    `%cevent: %c${event}, %cdata:`,
    'color: yellow;background: blue',
    'background: white',
    'background: orange; color: white',
    data,
  );
  // todo: 查看sensors文档，公共config
  // 点击事件上报基础数据
  const baseData: TDataClick = {
    apply_cooperatorId: getQueryString('sensors_apply_cooperatorId'),
    is_success: true,
    fail_reason: '',
    service_data_name: process.env.APP_NAME || '',
    ...extraTraceData,
  };

  // 当前合并手段只支持一层数据结构，如后续数据结构发生层次变化还需采用深拷贝
  const finalData = Object.assign({}, baseData, data);

  if (!collectors.size) return console.error('请先添加采集器');
  for (const col of collectors) {
    col?.track(event, finalData);
  }
};

/**
 * 点击事件上报封装，支持多报
 * @param events 多报场景：[newEvent, oldEvent]
 * @param datas  多报场景下，若需差异化上报数据，可传入数组：[newEventData, oldEventData]
 * @param adData 广告数据
 */
export const reportTrack = (
  events: string | string[],
  data: TTrackData = {},
  adData?: IAdData | null,
) => {
  // 处理广告数据
  const adAttrs = mapAdAttrs(adData);
  if (Array.isArray(events)) {
    // 多报场景
    // eslint-disable-next-line
    // @ts-ignore: 旧埋点暂不上报位置信息
    const { m_id, t_id, s_id, e_id, ...oldData } = data ?? {};
    events.forEach((event, index) => {
      // 序号0的为新事件，其他均为旧事件取处理后的数据
      let reportData = index === 0 ? data : oldData;
      // 若为差异化事件上报数据，则获取对应数据
      if (Array.isArray(data)) {
        reportData = data[index];
      }
      // TODO: 双报旧埋点排除位置信息，后续验证完毕，直接只上报数组第一个事件即可，再陆续删除双报代码
      reportTrackSingle(event, {
        ...adAttrs,
        ...(reportData ?? {}),
      });
    });
  } else {
    reportTrackSingle(events, {
      ...adAttrs,
      ...(data ?? {}),
    });
  }
};

const trackParams = (event, data) => {
  // 获得配置信息
  const { p_id, m_id, t_id, s_id, e_id, page_name, scenes } = getFullTrackData(data);

  // 获得对应值
  const elementP_id = getValueByFunction(p_id);
  const elementM_id = getValueByFunction(m_id);
  const elementT_id = getValueByFunction(t_id);
  const elementS_id = getValueByFunction(s_id);
  const elementE_id = getValueByFunction(e_id);

  const fakeDom: unknown = {
    dataset: {
      elementP_id,
      elementM_id,
      elementT_id,
      elementS_id,
      elementE_id,
    },
  };

  // 获得配置化内容，覆盖合并上报的内容
  const dom: HTMLElement = fakeDom as HTMLElement;
  let configData = {};
  if (airTagReady()) {
    configData = newGetConfigData(dom, event) ?? {};
  } else {
    configData = getConfigData(dom, event) ?? {};
  }
  data = Object.assign({}, data, configData);

  // 上报页面浏览事件时，通知app设置当前页面埋点关键信息
  if (event === 'page_view' && dsBridge.hasNativeMethod('UtilsModule.setWebPageBuriedParams')) {
    dsBridge.call(
      'UtilsModule.setWebPageBuriedParams',
      JSON.stringify({
        page_name: getValueByFunction(page_name),
        scenes: getValueByFunction(scenes),
        p_id: elementP_id,
      }),
    );
  }

  // todo: 查看sensors文档，公共config
  // 点击事件上报基础数据
  const baseData: TDataClick = {
    apply_cooperatorId: getQueryString('sensors_apply_cooperatorId'),
    is_success: true,
    fail_reason: '',
    service_data_name: process.env.APP_NAME || '',
    ...extraTraceData,
  };
  return {
    data,
    baseData,
  };
};

export const reportTrackStart = (event: string, data: TTrackData = {}) => {
  const { baseData, data: dataRes } = trackParams(event, data);

  // 当前合并手段只支持一层数据结构，如后续数据结构发生层次变化还需采用深拷贝
  const finalData = Object.assign({ maxWaitTime: 10 }, baseData, dataRes);
  console.info(
    `%cevent: %c${event}-trackStart, %cdata:`,
    'color: yellow;background: blue',
    'background: white',
    'background: orange; color: white',
    finalData,
  );

  if (!collectors.size) return console.error('请先添加采集器');
  for (const col of collectors) {
    if (col?.trackStart) {
      const tarct = col.trackStart(event);
      tarct.addProperties(finalData);
    }
  }
};

export const reportTrackEnd = (event: string, data: TTrackData = {}) => {
  const { baseData, data: dataRes } = trackParams(event, data);

  // 当前合并手段只支持一层数据结构，如后续数据结构发生层次变化还需采用深拷贝
  const finalData = Object.assign({}, baseData, dataRes);
  console.info(
    `%cevent: %c${event}-trackEnd, %cdata:`,
    'color: yellow;background: blue',
    'background: white',
    'background: orange; color: white',
    finalData,
  );

  if (!collectors.size) return console.error('请先添加采集器');
  for (const col of collectors) {
    if (col?.trackEnd) {
      col.trackAddProperties(event, finalData); // 为了处理注册公共属性 待sdk升级兼容后，此段代码后续可以删掉
      col.trackEnd(event, finalData);
    }
  }
};

// 页面预览封装
export const reportView = async (data: TTrackData) => {
  const baseData: TDataView = {
    //神策系统必须是1.4最新版及以上
    apply_no: '', // 依赖贷前接口，暂时不需要
    apply_cooperatorId: getQueryString('sensors_apply_cooperatorId'), // 历史项目是从/saveSensorsParam路由获取
    service_data_name: process.env.APP_NAME || '',
    ...extraTraceData,
  };

  // 当前合并手段只支持一层数据结构，如后续数据结构发生层次变化还需采用深拷贝
  const finalData = Object.assign({}, baseData, data);

  if (!collectors.size) console.error('请先添加采集器');
  for (const col of collectors) {
    col?.quick('autoTrack', finalData);
  }
};

// 上报方法登录
export const reportLogin = (newId?) => {
  const { userId } = useBaseStore();
  if (newId || userId) {
    for (const col of collectors) {
      col?.login(newId ? newId : userId);
    }
  }
};

// 注册页面离开事件
export const usePageLeave = () => {
  for (const col of collectors) {
    col?.use('PageLeave');
  }
};

// 默认全局属性配置
const resigterPageConf = {
  $title: () => getPageTitle(),
  page_name: () => getPageTitle(),
  current_page: () => getPageTitle(),
  channel_code: () => useBaseStore().cooperatorId,
  product_code: () => useBaseStore().productCode,
  app_key: () => useBaseStore().appKey,
  position: () => useBaseStore().sourceway,
  system: () => useBaseStore().system,
  apply_scene: () => {
    return msSessionStorage.get('apply_scene') || '';
  },
  new_or_old_system: 'new',
};

//全局注册强拉属性
const entrance = getQueryString('compelPull');
if (entrance) {
  msSessionStorage.set('compelPullEntrance', entrance);
  (resigterPageConf as any).entrance = () => entrance;
} else if (msSessionStorage.get('compelPullEntrance')) {
  (resigterPageConf as any).entrance = () => msSessionStorage.get('compelPullEntrance');
}
// 页面注册配置缓存
let pageConfCacke = resigterPageConf;

/**
 * 获得埋点上报完整数据（包括全局属性）
 * @param data
 */
export function getFullTrackData(data: TTrackData): TTrackData {
  return Object.assign({}, pageConfCacke, data ?? {});
}

/**
 * 注册全局属性
 * @param attrs
 */
export function registerPage(attrs: { [key: string]: any }) {
  if (attrs) {
    pageConfCacke = Object.assign(resigterPageConf, attrs);
    for (const col of collectors) {
      // 全局属性注册合并兜底默认配置
      col?.registerPage(pageConfCacke);
    }
  }
}

export function clearPageRegister(arr: boolean | string[]) {
  if (arr) {
    let i: number | string;
    // 清除指定参数
    if (isArray(arr) && arr.length > 0) {
      for (i = 0; i < arr.length; i++) {
        if (arr[i] in resigterPageConf) {
          delete resigterPageConf[arr[i]];
        }
      }
    } else if (arr === true) {
      // 清除全部
      for (i in resigterPageConf) {
        delete resigterPageConf[i];
      }
    }
  }
}

// 埋点注册
export const colsRegister = async () => {
  registerPage(resigterPageConf);
};
